import {
  Card,
  Select,
  selectState as ss,
  toast,
  type SelectMsg,
  type SelectState,
} from "@bfc-software/pallet";
import { match } from "ts-pattern";
// eslint-disable-next-line no-restricted-imports -- follow-up: use useCurrentLocalization()
import { TFunction } from "i18next";
import { logger } from "@bfc-software/app-toolbox";
import {
  fromQuery,
  type Async,
} from "@bfc-software/bfc-web-ui/lib/async-utils";

import {
  DIFFICULTY_BAND_OPTIONS,
  SUBSTITUTION_AUTHORIZED_OPTIONS,
  TASK_TYPE_ELIGIBILITY_OPTIONS,
  useUpdateUserProfileDefaults,
  useUserProfileDefaults,
  VALIDATION_LEVEL_OPTIONS,
  type UserProfileDefaults,
} from "../-data/warehouse-settings-client";
import styles from "./user-profile-card.module.css";

type ProfileItem = ss.Item; // { id: option string, label: option string }

/**
 * Pure; the one mapping all four fields share. `loaded` -> a live `always` on the
 * saved value; `loading` / `failed` -> `disabled` with their own reason.
 *
 * A local `match` rather than `selectUtils.fromAsync`: that maps an `Async` whose
 * loaded value IS the option list. Here the options are static and the `Async`
 * carries the selected value, and `loading` must be `disabled`, not a live field
 * with a spinner.
 */
function profileFieldState(a: {
  profile: Async<UserProfileDefaults>;
  field: keyof UserProfileDefaults; // which default this select edits
  label: string;
  options: ss.NonEmptyArray<string>;
  t: TFunction;
}): SelectState<ProfileItem> {
  const label = ss.visibleLabel({ text: a.label, orientation: "column" });
  return match(a.profile)
    .with({ kind: "loading" }, () =>
      ss.disabled({
        label,
        reason: a.t("systemControls.userProfile.loadingDefaults"),
      }),
    )
    .with({ kind: "failed" }, () =>
      ss.disabled({
        label,
        reason: a.t("systemControls.userProfile.defaultsFailed"),
      }),
    )
    .with({ kind: "loaded" }, ({ value }) => {
      const row = (id: string): ProfileItem => ({ id, label: id });
      const [first, ...rest] = a.options;
      return ss.always({
        label,
        options: ss.fixed<ProfileItem>([row(first), ...rest.map(row)]),
        // closed enum: build the selected row from the saved value, no lookup to miss
        selected: row(value[a.field]),
      });
    })
    .exhaustive();
}

export function UserProfileCard({
  orgId,
  warehouseId,
  t,
}: {
  orgId: string;
  warehouseId: string | null;
  t: TFunction;
}) {
  const query = useUserProfileDefaults(orgId, warehouseId);
  const update = useUpdateUserProfileDefaults(orgId, warehouseId);

  const set = (field: keyof UserProfileDefaults, value: string) => {
    update.mutate(
      { [field]: value },
      {
        onSuccess: () =>
          logger.info("Updated default user profile", { field, value }),
        onError: (error) => {
          logger.warn("Failed to update default user profile", {
            error,
            field,
          });
          toast.error(t("systemControls.userProfile.saveFailed"));
        },
      },
    );
  };

  const profile = fromQuery(query);

  /** One handler shape for all four; only `field` differs. */
  const onFieldMsg =
    (field: keyof UserProfileDefaults) => (msg: SelectMsg<ProfileItem>) =>
      match(msg)
        .with({ kind: "picked" }, ({ item }) => set(field, String(item.id)))
        .otherwise(() => {}); // `always`/`disabled`: nothing to clear or search

  return (
    <Card
      variant="panel"
      layout="body-only"
      data-test-role="user-profile-card"
      body={
        <>
          <div className={styles.title}>
            {t("systemControls.userProfile.title")}
          </div>
          <div className={styles.subtitle}>
            {t("systemControls.userProfile.subtitle")}
          </div>

          <div className={styles.fields}>
            <Select
              state={profileFieldState({
                profile,
                field: "defaultValidationLevel",
                label: t("systemControls.userProfile.validationLevel"),
                options: VALIDATION_LEVEL_OPTIONS,
                t,
              })}
              onMsg={onFieldMsg("defaultValidationLevel")}
            />
            <Select
              state={profileFieldState({
                profile,
                field: "defaultTaskTypeEligibility",
                label: t("systemControls.userProfile.taskTypeEligibility"),
                options: TASK_TYPE_ELIGIBILITY_OPTIONS,
                t,
              })}
              onMsg={onFieldMsg("defaultTaskTypeEligibility")}
            />
            <Select
              state={profileFieldState({
                profile,
                field: "defaultDifficultyBandCap",
                label: t("systemControls.userProfile.difficultyBandCap"),
                options: DIFFICULTY_BAND_OPTIONS,
                t,
              })}
              onMsg={onFieldMsg("defaultDifficultyBandCap")}
            />
            <Select
              state={profileFieldState({
                profile,
                field: "defaultSubstitutionAuthorized",
                label: t("systemControls.userProfile.substitutionAuthorized"),
                options: SUBSTITUTION_AUTHORIZED_OPTIONS,
                t,
              })}
              onMsg={onFieldMsg("defaultSubstitutionAuthorized")}
            />
          </div>
        </>
      }
    />
  );
}
